import { useState } from 'react';
import {
  Activity, AlertTriangle, BarChart3, CheckCircle2, ChevronRight,
  Code2, Cpu, Database, Loader2, RefreshCw, Server, Trash2, XCircle, Zap,
} from 'lucide-react';
import {
  getHealthMetrics,
  runMaintenanceAction,
  type HealthMetrics,
  type MaintenanceAction,
} from '../../api/admin';

// ── Types ─────────────────────────────────────────────────────────────────────

type Severity = 'ok' | 'warning' | 'critical';

interface Finding {
  priority: 'P0' | 'P1' | 'P2' | 'P3';
  category: string;
  issue: string;
  action: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function worst(...severities: Severity[]): Severity {
  if (severities.includes('critical')) return 'critical';
  if (severities.includes('warning')) return 'warning';
  return 'ok';
}

const SCOLORS: Record<Severity, { dot: string; text: string; border: string; bg: string }> = {
  critical: { dot: 'bg-red-500',     text: 'text-red-400',     border: 'border-red-800/60',     bg: 'bg-red-950/30' },
  warning:  { dot: 'bg-amber-500',   text: 'text-amber-400',   border: 'border-amber-800/60',   bg: 'bg-amber-950/30' },
  ok:       { dot: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-900/40', bg: 'bg-emerald-950/20' },
};

function computeFindings(d: HealthMetrics): Finding[] {
  const out: Finding[] = [];

  // Heap alarm uses heapUsedVsLimit (used / v8 hard cap), NOT heapUsedPercent (used / current
  // heapTotal). heapTotal starts small and V8 grows it on demand — 95% of 77 MB is fine when
  // the hard cap is 256 MB (real utilisation is only ~28%).
  const heapVsLimit = d.process.memory.heapUsedVsLimit;
  if (heapVsLimit >= 85)
    out.push({ priority: 'P1', category: 'Process', issue: `Heap at ${heapVsLimit}% of V8 limit (${d.process.memory.heapUsed} / ${d.process.memory.heapSizeLimit}) — OOM risk`, action: 'Profile for memory leaks with clinic.js; reduce --max-old-space-size or scale up RAM' });
  else if (heapVsLimit >= 65)
    out.push({ priority: 'P2', category: 'Process', issue: `Heap at ${heapVsLimit}% of V8 limit (${d.process.memory.heapUsed} / ${d.process.memory.heapSizeLimit}) — elevated`, action: 'Monitor trend; investigate if growing steadily over time' });

  // Process uptime
  if (d.process.uptimeSeconds < 300)
    out.push({ priority: 'P3', category: 'Process', issue: `Process uptime only ${fmtUptime(d.process.uptimeSeconds)} — recently restarted`, action: 'Check for crash loops; review Render deploy/restart logs' });

  // DB latency
  if (d.database.latencyMs > 200)
    out.push({ priority: 'P0', category: 'Database', issue: `DB latency ${d.database.latencyMs}ms — severely degraded`, action: 'Switch DATABASE_URL to Supabase PgBouncer port 6543 with ?pgbouncer=true&connection_limit=5. Direct port 5432 skips the pool.' });
  else if (d.database.latencyMs > 100)
    out.push({ priority: 'P1', category: 'Database', issue: `DB latency ${d.database.latencyMs}ms — above acceptable threshold`, action: 'Run EXPLAIN ANALYZE on the slowest queries; check if PgBouncer pooled URL (port 6543) is being used' });

  // DB waiting — most likely cause is direct connections saturating Supabase free-tier limit
  if (d.database.connections.waiting > 5)
    out.push({ priority: 'P0', category: 'Database', issue: `${d.database.connections.waiting} connections waiting — pool blocked`, action: 'Use Supabase PgBouncer URL (port 6543, ?pgbouncer=true&connection_limit=5). ALTER DATABASE work_mem has no effect via PgBouncer transaction mode.' });
  else if (d.database.connections.waiting > 0)
    out.push({ priority: 'P2', category: 'Database', issue: `${d.database.connections.waiting} connections waiting`, action: 'May be transient; ensure PgBouncer pooled URL is used, not direct port 5432' });

  // Temp files — pg_stat_database.temp_files is a LIFETIME cumulative counter (never resets
  // without pg_stat_reset, which Supabase blocks). Alarm on RATE (files/min), not total.
  const td = d.database.anomalies.tempFileDelta;
  const tempRate = td.perMinute; // null on first check (no prior snapshot)
  if (tempRate !== null && tempRate > 2)
    out.push({ priority: 'P1', category: 'Database', issue: `Temp files growing at ${tempRate}/min — queries spilling to disk`, action: 'Run: ALTER ROLE authenticator SET work_mem = \'64MB\'; — PostgREST introspection query needs more memory. Check pg_stat_statements for other spilling queries.' });
  else if (tempRate !== null && tempRate > 0)
    out.push({ priority: 'P3', category: 'Database', issue: `Temp files: ${td.sinceLastCheck} new in last ${td.intervalMinutes}m (${tempRate}/min) — slow spill`, action: 'Likely PostgREST schema refresh. Run: SELECT rolname, rolconfig FROM pg_roles WHERE rolname = \'authenticator\'; to confirm work_mem=32MB is set.' });
  else if (tempRate === null)
    out.push({ priority: 'P3', category: 'Database', issue: `Temp files total: ${d.database.anomalies.tempFiles.toLocaleString()} (${d.database.anomalies.tempBytes}) — lifetime counter, rate unknown on first check`, action: 'Refresh again in 5 min to see the per-minute rate. The total never resets on Supabase (pg_stat_reset is blocked).' });

  // Deadlocks
  if (d.database.anomalies.deadlocks > 0)
    out.push({ priority: 'P1', category: 'Database', issue: `${d.database.anomalies.deadlocks} deadlock(s) detected`, action: 'Review transaction ordering in code; add retry logic for deadlock errors (error code 40P01)' });

  // Redis — detect Upstash serverless: it always reports uptime=0 via INFO but accumulates hits
  const isUpstash = d.redis.uptimeSeconds === 0 && d.redis.keyspace.hits > 0;
  if (d.redis.uptimeSeconds === 0 && !isUpstash)
    out.push({ priority: 'P1', category: 'Redis', issue: 'Redis uptime 0s — recently restarted, cache is cold', action: 'Enable AOF persistence (appendonly yes); investigate restart via Redis logs' });
  if (isUpstash)
    out.push({ priority: 'P3', category: 'Redis', issue: `Upstash serverless — uptime always shows 0 (expected behavior, ${d.redis.keyspace.hits.toLocaleString()} hits confirm service is healthy)`, action: 'No action needed. Upstash does not expose real uptime via the INFO command.' });

  // Redis no persistence — Upstash free tier has no persistence by design, suppress false alarm
  if (!d.redis.persistence.rdbEnabled && !d.redis.persistence.aofEnabled && !isUpstash)
    out.push({ priority: 'P2', category: 'Redis', issue: 'Redis persistence disabled — all cached data lost on restart', action: 'Enable appendonly yes in redis.conf for AOF durability' });
  if (!d.redis.persistence.rdbEnabled && !d.redis.persistence.aofEnabled && isUpstash)
    out.push({ priority: 'P3', category: 'Redis', issue: 'Upstash free tier — no RDB/AOF persistence (expected)', action: 'Cache is comment-fetch only; loss on restart is safe. Upgrade Upstash plan for persistence if needed.' });

  // Redis hit rate
  const hr = d.redis.keyspace.cacheHitRate;
  if (hr !== null && hr < 30)
    out.push({ priority: 'P3', category: 'Redis', issue: `Cache hit rate ${hr}% — low (cold or poor key utilization)`, action: 'Monitor over 30 min post-restart; if persistent, review TTL settings and access patterns' });

  // OS — on containerised platforms (Render, Railway, Fly.io), Node's os.cpus() and os.freemem()
  // report the HOST machine, not the container's limits. A host with > 4 GB RAM is a strong
  // signal we are seeing shared-host stats — load/memory figures are NOT the user's container.
  const likelyContainerHost = d.os.memory.totalBytes > 4 * 1024 * 1024 * 1024;

  if (likelyContainerHost) {
    out.push({ priority: 'P3', category: 'OS', issue: `OS stats reflect the shared host machine (${d.os.cpus} CPUs, ${d.os.memory.total} RAM) — not the Render 512 MB container`, action: 'Ignore OS load/memory readings. Monitor container limits via Render dashboard metrics instead.' });
  } else {
    const lpc = d.os.loadAvg['1m'] / d.os.cpus;
    if (lpc >= 1.0)
      out.push({ priority: 'P1', category: 'OS', issue: `CPU saturated — ${d.os.loadAvg['1m']} load on ${d.os.cpus} cores (${(lpc * 100).toFixed(0)}% per core)`, action: 'Identify hot processes with top/htop; consider horizontal scaling' });
    else if (lpc >= 0.7)
      out.push({ priority: 'P2', category: 'OS', issue: `CPU load elevated — ${d.os.loadAvg['1m']} on ${d.os.cpus} cores`, action: 'Monitor trend; profile Node CPU with --prof if sustained above 70%' });

    if (d.os.memory.freePercent < 10)
      out.push({ priority: 'P0', category: 'OS', issue: `OS memory critically low — ${d.os.memory.free} free (${d.os.memory.freePercent}%)`, action: 'Reduce Node heap and Redis maxmemory; scale up RAM immediately' });
    else if (d.os.memory.freePercent < 20)
      out.push({ priority: 'P2', category: 'OS', issue: `OS memory low — ${d.os.memory.free} free (${d.os.memory.freePercent}%)`, action: 'Monitor growth rate; plan RAM increase before hitting critical threshold' });
  }

  const order: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 };
  return out.sort((a, b) => order[a.priority] - order[b.priority]);
}

// ── JSON syntax highlighter ───────────────────────────────────────────────────

function syntaxHighlight(raw: string): string {
  const esc = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc.replace(
    /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (m) => {
      if (/^"/.test(m)) {
        return /:$/.test(m)
          ? `<span class="text-sky-300">${m}</span>`
          : `<span class="text-amber-300">${m}</span>`;
      }
      if (/true|false/.test(m)) return `<span class="text-violet-400">${m}</span>`;
      if (/null/.test(m))        return `<span class="text-red-400">${m}</span>`;
      return `<span class="text-emerald-400">${m}</span>`;
    },
  );
}

// ── Shared UI primitives ──────────────────────────────────────────────────────

function Dot({ s }: { s: Severity }) {
  return <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${SCOLORS[s].dot}`} />;
}

function Badge({ s }: { s: Severity }) {
  const c = SCOLORS[s];
  const label = s === 'ok' ? 'Healthy' : s === 'warning' ? 'Warning' : 'Critical';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${c.text} ${c.border} ${c.bg}`}>
      <Dot s={s} />{label}
    </span>
  );
}

function MiniBar({ pct, s }: { pct: number; s: Severity }) {
  const color = s === 'critical' ? 'bg-red-500' : s === 'warning' ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="mt-1 h-1 w-full rounded-full bg-zinc-800 overflow-hidden">
      <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

function Row({ label, value, s = 'ok', sub }: { label: string; value: string | number; s?: Severity; sub?: string }) {
  const vc = s === 'critical' ? 'text-red-300' : s === 'warning' ? 'text-amber-300' : 'text-zinc-100';
  return (
    <div className="flex items-start justify-between py-2 border-b border-zinc-800/40 last:border-0 gap-3 min-w-0">
      <div className="flex items-center gap-2 min-w-0 flex-shrink-0 w-48">
        <Dot s={s} />
        <span className="text-xs text-zinc-400 truncate">{label}</span>
      </div>
      <div className="text-right min-w-0">
        <span className={`text-xs font-medium ${vc} break-all`}>{value}</span>
        {sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function Card({
  title, icon: Icon, iconBg, s, children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  s: Severity;
  children: React.ReactNode;
}) {
  const c = SCOLORS[s];
  return (
    <div className={`rounded-xl border ${c.border} bg-zinc-950/70 p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${iconBg}`}>
            <Icon className={`w-3.5 h-3.5 ${c.text}`} />
          </div>
          <span className="text-sm font-semibold text-zinc-100">{title}</span>
        </div>
        <Badge s={s} />
      </div>
      {children}
    </div>
  );
}

// ── Section cards ─────────────────────────────────────────────────────────────

function ProcessCard({ d }: { d: HealthMetrics['process'] }) {
  const heapS: Severity = d.memory.heapUsedVsLimit >= 85 ? 'critical' : d.memory.heapUsedVsLimit >= 65 ? 'warning' : 'ok';
  const uptimeS: Severity = d.uptimeSeconds < 300 ? 'warning' : 'ok';
  const s = worst(heapS, uptimeS);
  return (
    <Card title="Node.js Process" icon={Server} iconBg="bg-sky-500/10" s={s}>
      <Row label="Node version" value={d.nodeVersion} />
      <Row label="PID" value={d.pid} />
      <Row label="Uptime" value={fmtUptime(d.uptimeSeconds)} s={uptimeS} sub={d.uptimeSeconds < 300 ? 'Recently restarted' : undefined} />
      <Row
        label="Heap used / V8 limit"
        value={`${d.memory.heapUsed} / ${d.memory.heapSizeLimit}`}
        s={heapS}
        sub={`${d.memory.heapUsedVsLimit}% of limit · heapTotal ${d.memory.heapTotal} (V8 current allocation, grows on demand)`}
      />
      <MiniBar pct={d.memory.heapUsedVsLimit} s={heapS} />
      <div className="mt-2">
        <Row label="RSS" value={d.memory.rss} />
        <Row label="External / buffers" value={`${d.memory.external} / ${d.memory.arrayBuffers}`} />
        <Row label="CPU time" value={`${(d.cpu.userMs + d.cpu.systemMs).toLocaleString()} ms`} sub={`${d.cpu.userMs.toLocaleString()} user + ${d.cpu.systemMs.toLocaleString()} sys`} />
      </div>
    </Card>
  );
}

function OsCard({ d }: { d: HealthMetrics['os'] }) {
  const lpc = d.loadAvg['1m'] / d.cpus;
  const likelyContainerHost = d.memory.totalBytes > 4 * 1024 * 1024 * 1024;
  const loadS: Severity = likelyContainerHost ? 'ok' : lpc >= 1.0 ? 'critical' : lpc >= 0.7 ? 'warning' : 'ok';
  const memS: Severity  = likelyContainerHost ? 'ok' : d.memory.freePercent < 10 ? 'critical' : d.memory.freePercent < 20 ? 'warning' : 'ok';
  const s = worst(loadS, memS);
  const trendDown = d.loadAvg['15m'] > d.loadAvg['5m'] && d.loadAvg['5m'] > d.loadAvg['1m'];
  return (
    <Card title="Operating System" icon={Cpu} iconBg="bg-violet-500/10" s={s}>
      {likelyContainerHost && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-900/40 bg-amber-950/20 px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-300 leading-relaxed">
            These stats reflect the <strong>shared host machine</strong> ({d.cpus} CPUs, {d.memory.total} RAM), not the Render 512 MB container.
            Load and memory readings below are not actionable for your instance.
          </p>
        </div>
      )}
      <Row label="Platform / arch" value={`${d.platform} / ${d.arch}`} />
      <Row label="CPU cores" value={`${d.cpus}${likelyContainerHost ? ' (host)' : ''}`} />
      <Row
        label="Load avg 1m / 5m / 15m"
        value={`${d.loadAvg['1m']} / ${d.loadAvg['5m']} / ${d.loadAvg['15m']}`}
        s={loadS}
        sub={likelyContainerHost ? 'Host machine — not your container' : trendDown ? 'Trend: declining ↓ (good)' : 'Trend: stable / rising'}
      />
      <Row label="Load per CPU (1m)" value={`${(lpc * 100).toFixed(0)}%`} s={loadS} sub={likelyContainerHost ? 'Host-level — ignore for container sizing' : undefined} />
      <Row
        label="RAM free"
        value={`${d.memory.free} / ${d.memory.total}`}
        s={memS}
        sub={likelyContainerHost ? 'Host machine RAM — use Render dashboard for container limits' : `${d.memory.freePercent}% free`}
      />
      <MiniBar pct={100 - d.memory.freePercent} s={memS} />
    </Card>
  );
}

function RedisCard({ d }: { d: HealthMetrics['redis'] }) {
  const isUpstash = d.uptimeSeconds === 0 && d.keyspace.hits > 0;
  const statusS: Severity = d.status !== 'ok' ? 'critical' : 'ok';
  const uptimeS: Severity = d.uptimeSeconds === 0 && !isUpstash ? 'warning' : 'ok';
  const persistS: Severity = !d.persistence.rdbEnabled && !d.persistence.aofEnabled && !isUpstash ? 'warning' : 'ok';
  const hr = d.keyspace.cacheHitRate;
  const hitS: Severity = hr !== null && hr < 30 ? 'warning' : 'ok';
  const blockedS: Severity = d.clients.blocked > 0 ? 'warning' : 'ok';
  const s = worst(statusS, uptimeS, persistS);
  return (
    <Card title="Redis" icon={Zap} iconBg="bg-amber-500/10" s={s}>
      <Row label="Status / version" value={`${d.status} · v${d.version ?? '?'}`} s={statusS} />
      <Row label="Mode" value={`${d.mode}${isUpstash ? ' · Upstash serverless' : ''}`} />
      <Row
        label="Uptime"
        value={isUpstash ? 'n/a (Upstash serverless)' : d.uptimeSeconds === 0 ? '< 1s — just restarted' : fmtUptime(d.uptimeSeconds)}
        s={uptimeS}
        sub={isUpstash ? 'Always 0 via INFO — not a restart indicator' : uptimeS === 'warning' ? 'Cache warming up' : undefined}
      />
      <Row label="Memory used / max" value={`${d.memory.used ?? '—'} / ${d.memory.maxmemory ?? '—'}`} />
      <Row
        label="Clients"
        value={`${d.clients.connected} connected, ${d.clients.blocked} blocked`}
        s={blockedS}
      />
      <Row label="Total keys" value={d.keyspace.totalKeys.toLocaleString()} />
      <Row
        label="Cache hit rate"
        value={hr !== null ? `${hr}%` : 'n/a'}
        s={hitS}
        sub={`${d.keyspace.hits.toLocaleString()} hits / ${d.keyspace.misses.toLocaleString()} misses`}
      />
      {hr !== null && <MiniBar pct={hr} s={hitS} />}
      <div className="mt-2">
        <Row label="Ops/sec" value={d.throughput.opsPerSec} sub={`${d.throughput.commandsProcessed.toLocaleString()} total processed`} />
        <Row
          label="Persistence"
          value={d.persistence.rdbEnabled ? 'RDB on' : d.persistence.aofEnabled ? 'AOF on' : 'Disabled'}
          s={persistS}
          sub={persistS === 'warning' ? 'Data lost on restart' : 'Durability active'}
        />
      </div>
    </Card>
  );
}

function DbCard({ d }: { d: HealthMetrics['database'] }) {
  const statusS: Severity = d.status !== 'ok' ? 'critical' : 'ok';
  const latS: Severity = d.latencyMs > 200 ? 'critical' : d.latencyMs > 100 ? 'warning' : 'ok';
  const waitS: Severity = d.connections.waiting > 5 ? 'critical' : d.connections.waiting > 0 ? 'warning' : 'ok';
  const bufS: Severity = d.bufferHitRate < 90 ? 'warning' : 'ok';
  const td = d.anomalies.tempFileDelta;
  const tmpRate = td.perMinute;
  const tmpS: Severity = tmpRate !== null ? (tmpRate > 2 ? 'warning' : 'ok') : 'ok';
  const dlS: Severity = d.anomalies.deadlocks > 0 ? 'warning' : 'ok';
  const s = worst(statusS, latS, waitS, tmpS, dlS);
  const rollbackRate = d.transactions.committed > 0
    ? ((d.transactions.rolledBack / d.transactions.committed) * 100).toFixed(3)
    : '0';
  return (
    <Card title="PostgreSQL" icon={Database} iconBg="bg-emerald-500/10" s={s}>
      <Row label="Status / provider" value={`${d.status} · ${d.provider}`} s={statusS} />
      <Row
        label="Ping latency"
        value={`${d.latencyMs} ms`}
        s={latS}
        sub={latS !== 'ok' ? 'Check for blocking transactions' : 'Normal'}
      />
      <Row label="DB size" value={d.size} />
      <Row label="Connections" value={`${d.connections.active} active / ${d.connections.idle} idle / ${d.connections.total} total`} />
      <Row
        label="Waiting connections"
        value={d.connections.waiting}
        s={waitS}
        sub={waitS !== 'ok' ? 'Switch DATABASE_URL to PgBouncer port 6543 to fix' : 'Clear'}
      />
      <Row label="Buffer hit rate" value={`${d.bufferHitRate}%`} s={bufS} />
      <Row
        label="Temp files (rate)"
        value={tmpRate !== null ? `${tmpRate}/min` : 'first check'}
        s={tmpS}
        sub={
          tmpRate !== null
            ? `${td.sinceLastCheck} new in last ${td.intervalMinutes}m · total ${d.anomalies.tempFiles.toLocaleString()} (lifetime counter)`
            : `Total ${d.anomalies.tempFiles.toLocaleString()} (${d.anomalies.tempBytes}) — refresh again for rate`
        }
      />
      <Row
        label="Transactions"
        value={`${d.transactions.committed.toLocaleString()} committed`}
        sub={`${d.transactions.rolledBack} rollbacks (${rollbackRate}%)`}
      />
      <Row label="Deadlocks" value={d.anomalies.deadlocks} s={dlS} />
      <Row label="Conflicts" value={d.anomalies.conflicts} s={d.anomalies.conflicts > 0 ? 'warning' : 'ok'} />
    </Card>
  );
}

// ── Findings panel ────────────────────────────────────────────────────────────

const P_COLORS: Record<string, string> = {
  P0: 'text-red-400 border-red-800/60 bg-red-950/30',
  P1: 'text-orange-400 border-orange-800/60 bg-orange-950/30',
  P2: 'text-amber-400 border-amber-800/60 bg-amber-950/20',
  P3: 'text-sky-400 border-sky-800/60 bg-sky-950/20',
};

function FindingsPanel({ findings }: { findings: Finding[] }) {
  if (!findings.length) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl border border-emerald-900/40 bg-emerald-950/20">
        <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-emerald-300">All systems healthy</p>
          <p className="text-xs text-emerald-700 mt-0.5">No actionable findings across process, OS, Redis, and database.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-amber-400" />
        <p className="text-sm font-semibold text-zinc-100">Findings &amp; Recommended Actions</p>
        <span className="ml-auto text-[11px] text-zinc-500">{findings.length} issue{findings.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="flex flex-col gap-2">
        {findings.map((f, i) => (
          <div key={i} className="flex gap-3 rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
            <span className={`flex-shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold border ${P_COLORS[f.priority]}`}>{f.priority}</span>
            <div className="min-w-0">
              <div className="flex items-start gap-2 flex-wrap">
                <span className="text-xs font-medium text-zinc-200">{f.issue}</span>
                <span className="text-[10px] text-zinc-600 border border-zinc-800 px-1.5 py-0.5 rounded flex-shrink-0">{f.category}</span>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1 flex items-start gap-1">
                <ChevronRight className="w-3 h-3 flex-shrink-0 mt-0.5 text-zinc-600" />
                {f.action}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Quick-fix definitions ─────────────────────────────────────────────────────

const FIXES: Array<{
  id: MaintenanceAction;
  label: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  danger?: boolean;
}> = [
  {
    id: 'clear_redis_cache',
    label: 'Clear Redis Cache',
    desc: 'Delete all yt_comment:* keys from Upstash. Frees daily command quota and forces fresh cache warm-up.',
    icon: Zap,
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-400',
  },
  {
    id: 'analyze_database',
    label: 'Analyse Database',
    desc: 'Run ANALYZE on Supabase PostgreSQL to refresh query planner statistics and reduce temp-file spill.',
    icon: Database,
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-400',
  },
  {
    id: 'reset_connections',
    label: 'Reset DB Connections',
    desc: 'Disconnect Prisma pool and reconnect lazily. Releases idle/waiting connections back to PgBouncer.',
    icon: RefreshCw,
    iconBg: 'bg-sky-500/10',
    iconColor: 'text-sky-400',
  },
  {
    id: 'clear_failed_analyses',
    label: 'Clear Failed Analyses',
    desc: 'Delete failed/cancelled analysis records older than 7 days. Reclaims Supabase 500 MB free storage.',
    icon: Trash2,
    iconBg: 'bg-red-500/10',
    iconColor: 'text-red-400',
    danger: true,
  },
  {
    id: 'clear_old_analysis_json',
    label: 'Free Old Report JSON',
    desc: 'Nullify analysis JSON blobs on completed reports older than 90 days. History entry stays, full JSON freed.',
    icon: Trash2,
    iconBg: 'bg-orange-500/10',
    iconColor: 'text-orange-400',
    danger: true,
  },
];

type FixPhase = 'idle' | 'loading' | 'ok' | 'error';

function QuickFixes() {
  const [states, setStates]   = useState<Record<string, FixPhase>>({});
  const [results, setResults] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<string | null>(null);

  const fire = async (id: MaintenanceAction) => {
    setStates(s => ({ ...s, [id]: 'loading' }));
    setResults(r => ({ ...r, [id]: '' }));
    setConfirm(null);
    try {
      const res = await runMaintenanceAction(id);
      setStates(s => ({ ...s, [id]: 'ok' }));
      setResults(r => ({ ...r, [id]: res.data.data.message }));
    } catch (e: unknown) {
      setStates(s => ({ ...s, [id]: 'error' }));
      setResults(r => ({ ...r, [id]: e instanceof Error ? e.message : 'Action failed' }));
    }
  };

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-zinc-800">
          <Zap className="w-3.5 h-3.5 text-zinc-400" />
        </div>
        <p className="text-sm font-semibold text-zinc-100">Quick Fixes</p>
        <span className="ml-2 text-[11px] text-zinc-600 border border-zinc-800 rounded px-1.5 py-0.5">Free-tier safe</span>
        <span className="ml-auto text-[11px] text-zinc-500">Upstash · Supabase · Render</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {FIXES.map(({ id, label, desc, icon: Icon, iconBg, iconColor, danger }) => {
          const ph = states[id] ?? 'idle';
          const msg = results[id] ?? '';
          const isLoading = ph === 'loading';
          const waitingConfirm = confirm === id;

          return (
            <div
              key={id}
              className={`flex flex-col rounded-xl border p-3.5 transition-colors ${
                ph === 'ok'    ? 'border-emerald-900/50 bg-emerald-950/20' :
                ph === 'error' ? 'border-red-900/50 bg-red-950/20' :
                danger         ? 'border-zinc-800 bg-zinc-900/60 hover:border-zinc-700' :
                                 'border-zinc-800 bg-zinc-900/60 hover:border-zinc-700'
              }`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-lg ${iconBg}`}>
                  <Icon className={`w-4 h-4 ${iconColor}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-zinc-200">{label}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </div>

              {/* Result message */}
              {msg && (
                <p className={`text-[11px] mb-2 leading-relaxed px-2 py-1.5 rounded-lg border ${
                  ph === 'ok'
                    ? 'text-emerald-300 bg-emerald-950/30 border-emerald-900/40'
                    : 'text-red-300 bg-red-950/30 border-red-900/40'
                }`}>
                  {msg}
                </p>
              )}

              {/* Action button */}
              <div className="mt-auto">
                {danger && !waitingConfirm && ph !== 'ok' ? (
                  <button
                    disabled={isLoading}
                    onClick={() => setConfirm(id)}
                    className="w-full inline-flex items-center justify-center gap-1.5 h-7 px-3 rounded-lg border border-red-900/50 text-red-400 hover:bg-red-950/30 transition-colors text-xs disabled:opacity-50"
                  >
                    {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    {isLoading ? 'Running…' : 'Run'}
                  </button>
                ) : danger && waitingConfirm ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => fire(id as MaintenanceAction)}
                      className="flex-1 inline-flex items-center justify-center h-7 px-3 rounded-lg bg-red-900/40 border border-red-800/60 text-red-300 hover:bg-red-900/60 transition-colors text-xs font-medium"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirm(null)}
                      className="flex-1 inline-flex items-center justify-center h-7 px-3 rounded-lg border border-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    disabled={isLoading || ph === 'ok'}
                    onClick={() => fire(id as MaintenanceAction)}
                    className={`w-full inline-flex items-center justify-center gap-1.5 h-7 px-3 rounded-lg border transition-colors text-xs ${
                      ph === 'ok'
                        ? 'border-emerald-900/40 text-emerald-500 cursor-default'
                        : 'border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 disabled:opacity-50'
                    }`}
                  >
                    {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> :
                     ph === 'ok' ? <CheckCircle2 className="w-3 h-3" /> :
                     <Activity className="w-3 h-3" />}
                    {isLoading ? 'Running…' : ph === 'ok' ? 'Done' : 'Run'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function HealthReport() {
  const [phase, setPhase]     = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [data, setData]       = useState<HealthMetrics | null>(null);
  const [errMsg, setErrMsg]   = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  const run = async () => {
    setPhase('loading');
    setErrMsg(null);
    try {
      const res = await getHealthMetrics();
      setData(res.data.data);
      setPhase('loaded');
      setShowRaw(false);
    } catch (e: unknown) {
      setErrMsg(e instanceof Error ? e.message : 'Failed to fetch health metrics');
      setPhase('error');
    }
  };

  // ── Idle ──
  if (phase === 'idle') {
    return (
      <div className="flex flex-col items-center justify-center py-14 gap-3 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-800 border border-zinc-700">
          <Activity className="w-5 h-5 text-zinc-400" />
        </div>
        <p className="text-sm text-zinc-500 max-w-xs">
          Health metrics are fetched on demand — they are not auto-polled to avoid unnecessary server load.
        </p>
        <button
          onClick={run}
          className="mt-1 inline-flex items-center gap-2 h-9 px-5 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 text-sm font-medium transition-colors"
        >
          <Activity className="w-4 h-4" /> Run Health Check
        </button>
      </div>
    );
  }

  // ── Loading ──
  if (phase === 'loading') {
    return (
      <div className="flex items-center justify-center py-14 gap-2 text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Collecting metrics from server…</span>
      </div>
    );
  }

  // ── Error ──
  if (phase === 'error') {
    return (
      <div className="flex flex-col items-center py-12 gap-3 text-center">
        <XCircle className="w-6 h-6 text-red-500" />
        <p className="text-sm text-red-400 max-w-sm">{errMsg}</p>
        <button onClick={run} className="text-xs text-zinc-400 hover:text-zinc-200 underline underline-offset-2 transition-colors">
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  // ── Derived severity ──
  const pSev: Severity = data.process.memory.heapUsedVsLimit >= 85 ? 'critical' : data.process.memory.heapUsedVsLimit >= 65 ? 'warning' : 'ok';
  const lpc = data.os.loadAvg['1m'] / data.os.cpus;
  const likelyContainerHost = data.os.memory.totalBytes > 4 * 1024 * 1024 * 1024; // host machine — OS stats are not container stats
  const oSev: Severity = likelyContainerHost ? 'ok' : lpc >= 1.0 || data.os.memory.freePercent < 10 ? 'critical' : lpc >= 0.7 || data.os.memory.freePercent < 20 ? 'warning' : 'ok';
  const isUpstashRedis = data.redis.uptimeSeconds === 0 && data.redis.keyspace.hits > 0;
  const rSev: Severity = data.redis.status !== 'ok' ? 'critical' : (!isUpstashRedis && (data.redis.uptimeSeconds === 0 || (!data.redis.persistence.rdbEnabled && !data.redis.persistence.aofEnabled))) ? 'warning' : 'ok';
  const _tmpRate = data.database.anomalies.tempFileDelta.perMinute;
  const dSev: Severity = data.database.status !== 'ok' ? 'critical'
    : (data.database.latencyMs > 200 || data.database.connections.waiting > 5 || (_tmpRate !== null && _tmpRate > 2)) ? 'critical'
    : (data.database.latencyMs > 100 || data.database.connections.waiting > 0) ? 'warning'
    : 'ok';
  const globalSev = worst(pSev, oSev, rSev, dSev);
  const findings = computeFindings(data);
  const collectedAt = new Date(data.collectedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });

  return (
    <div className="space-y-4">
      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Badge s={globalSev} />
          <span className="text-xs text-zinc-500">Collected {collectedAt}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Report / Raw toggle */}
          <div className="flex items-center gap-0.5 rounded-lg border border-zinc-700 p-0.5 bg-zinc-900">
            <button
              onClick={() => setShowRaw(false)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${!showRaw ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200'}`}
            >
              <BarChart3 className="w-3 h-3" /> Report
            </button>
            <button
              onClick={() => setShowRaw(true)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${showRaw ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200'}`}
            >
              <Code2 className="w-3 h-3" /> Raw JSON
            </button>
          </div>
          <button
            onClick={run}
            className="inline-flex items-center gap-1.5 h-7 px-3 rounded-lg border border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors text-xs"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {showRaw ? (
        /* ── Raw JSON ── */
        <div className="rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-900/60">
            <span className="text-xs font-mono text-zinc-500">GET /health/metrics</span>
            <span className="text-xs text-zinc-600">{new Blob([JSON.stringify(data)]).size.toLocaleString()} bytes</span>
          </div>
          <div className="overflow-auto max-h-[72vh] p-4">
            <pre
              className="text-xs font-mono leading-5 whitespace-pre text-zinc-400"
              dangerouslySetInnerHTML={{ __html: syntaxHighlight(JSON.stringify(data, null, 2)) }}
            />
          </div>
        </div>
      ) : (
        /* ── Formatted report ── */
        <>
          <FindingsPanel findings={findings} />
          <QuickFixes />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ProcessCard d={data.process} />
            <OsCard d={data.os} />
            <RedisCard d={data.redis} />
            <DbCard d={data.database} />
          </div>
        </>
      )}
    </div>
  );
}
